// Shown while multiple papers are being evaluated sequentially.
// Displays a progress bar and per-paper status so users know
// exactly what's happening and how long to wait.

import React from 'react';
import type { Theme } from '../types';
import ThemeToggle from './ThemeToggle';
import loadingStyles from '../styles/LoadingState.module.css';
import batchProgressStyles from '../styles/BatchProgress.module.css';

interface BatchProgressProps {
  files: File[];
  current: number;
  theme: Theme;
  onToggleTheme: () => void;
}

export default function BatchProgress({
  files,
  current,
  theme,
  onToggleTheme,
}: BatchProgressProps) {
  const pct = Math.round((current / files.length) * 100);

  return (
    <div className={batchProgressStyles.page}>
      <h1 className={batchProgressStyles.title}>Desk Reject</h1>
      <p className={batchProgressStyles.subtitle}>
        Evaluating {current + 1} of {files.length} papers...
      </p>

      <div className={batchProgressStyles.progressBar}>
        <div
          className={batchProgressStyles.progressFill}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className={batchProgressStyles.papers}>
        {files.map((f, i) => (
          <div
            key={i}
            className={`${batchProgressStyles.paperRow} ${
              i === current ? batchProgressStyles.paperRowActive :
              i < current ? batchProgressStyles.paperRowDone : ''
            }`}
          >
            {i < current ? (
              <span className={batchProgressStyles.statusIcon}>✓</span>
            ) : i === current ? (
              <div className={batchProgressStyles.spinner} />
            ) : (
              <span className={batchProgressStyles.statusIcon}>○</span>
            )}
            <span className={batchProgressStyles.paperName}>{f.name}</span>
            <span className={batchProgressStyles.paperStatus}>
              {i < current ? 'done' : i === current ? 'evaluating' : 'waiting'}
            </span>
          </div>
        ))}
      </div>

      {/* Theme-specific waiting animation */}
      {theme === 'scholar' ? (
        <div className={loadingStyles.hourglassWrap} style={{ marginTop: 40 }}>
          <div className={loadingStyles.hourglassTop} />
          <div className={loadingStyles.hourglassNeck} />
          {Array.from({ length: 10 }, (_, i) => (
            <div
              key={i}
              className={`${loadingStyles.sandDrop} ${loadingStyles[`sandDrop${i + 1}` as keyof typeof loadingStyles]}`}
            />
          ))}
          <div className={loadingStyles.hourglassBottom} />
        </div>
      ) : (
        <div className={loadingStyles.radar} style={{ marginTop: 40 }}>
          <div className={`${loadingStyles.radarRing} ${loadingStyles.radarRing1}`} />
          <div className={`${loadingStyles.radarRing} ${loadingStyles.radarRing2}`} />
          <div className={`${loadingStyles.radarRing} ${loadingStyles.radarRing3}`} />
          <div className={loadingStyles.radarSweep} />
          <div className={loadingStyles.radarPing} />
          <div className={loadingStyles.radarDot} />
        </div>
      )}

      <ThemeToggle theme={theme} onToggle={onToggleTheme} />
    </div>
  );
}